import {AdminSidebarIcons} from '@app/admin/admin-config';
import {driveSidebarIcons} from '@app/drive/layout/sidebar/drive-sidebar-icons';
import {useAuth} from '@common/auth/use-auth';
import {
  SettingsNavConfig,
  SettingsNavGroups,
  SettingsNavItem,
} from '@common/admin/settings/settings-nav-config';
import {Command} from '@shadcn/command/command';
import {Dialog} from '@shadcn/dialog/dialog';
import {Kbd} from '@shadcn/kbd';
import {useSettings} from '@ui/settings/use-settings';
import {Trans} from '@ui/i18n/trans';
import {cn} from '@ui/utils/cn';
import {
  BellIcon,
  BookOpenIcon,
  HardDriveIcon,
  KeyIcon,
  LockIcon,
  PaletteIcon,
  SearchIcon,
  SettingsIcon,
  StoreIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import {ReactElement, ReactNode, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';

export const OPEN_COMMAND_PALETTE = 'cd-command-palette';

export type CommandPaletteScope = 'site' | 'drive' | 'account' | 'admin' | 'api';

export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_COMMAND_PALETTE));
}

export function isMacShortcut() {
  return (
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
  );
}

export function resolveCommandPaletteScope(pathname: string): CommandPaletteScope {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/api-docs') || pathname.startsWith('/developers')) {
    return 'api';
  }
  if (pathname.startsWith('/account-settings')) return 'account';
  if (pathname.startsWith('/drive')) return 'drive';
  return 'site';
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const scope = resolveCommandPaletteScope(pathname);
  const groups = useCommandGroups(scope);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        // API docs owns its own method search — do not open site/admin palette there.
        if (resolveCommandPaletteScope(window.location.pathname) === 'api') {
          return;
        }
        event.preventDefault();
        setOpen(current => !current);
      }
    };
    const onOpen = () => {
      if (resolveCommandPaletteScope(window.location.pathname) === 'api') {
        return;
      }
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_COMMAND_PALETTE, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_COMMAND_PALETTE, onOpen);
    };
  }, []);

  const go = (href: string) => {
    setOpen(false);
    queueMicrotask(() => {
      if (href.startsWith('http')) {
        window.location.assign(href);
        return;
      }
      navigate(href);
    });
  };

  const placeholder =
    scope === 'admin'
      ? 'Search admin…'
      : scope === 'account'
        ? 'Search account…'
        : 'Search Drive and account…';

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Command.DialogContent className="top-[12%] max-w-xl translate-y-0">
          <Command.Root items={groups} itemToStringValue={item => item.search}>
            <Command.Input placeholder={placeholder} />
            <Command.Empty>
              <Trans message="No matching pages." />
            </Command.Empty>
            <Command.List>
              {group => (
                <Command.Group key={group.value} items={group.items}>
                  <Command.GroupLabel>{group.label}</Command.GroupLabel>
                  <Command.Collection>
                    {item => (
                      <Command.Item
                        key={item.id}
                        value={item.search}
                        onSelect={() => go(item.href)}
                      >
                        <span className="text-muted-foreground">{item.icon}</span>
                        <span className="min-w-0 truncate">{item.label}</span>
                      </Command.Item>
                    )}
                  </Command.Collection>
                </Command.Group>
              )}
            </Command.List>
          </Command.Root>
          <Command.DialogFooter />
        </Command.DialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function CommandPaletteTrigger({
  className,
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  const mac = isMacShortcut();
  const {pathname} = useLocation();
  if (resolveCommandPaletteScope(pathname) === 'api') {
    return null;
  }
  return (
    <button
      type="button"
      onClick={openCommandPalette}
      className={cn(
        'inline-flex h-8 items-center gap-2 rounded-lg border bg-background px-2 text-xs text-muted-foreground hover:bg-muted',
        className,
      )}
      aria-label="Open search"
    >
      <SearchIcon className="size-3.5" />
      {compact ? null : <span className="hidden sm:inline">Search</span>}
      <Kbd className="hidden sm:inline-flex">{mac ? '⌘K' : 'Ctrl K'}</Kbd>
    </button>
  );
}

type PaletteItem = {
  id: string;
  label: string;
  search: string;
  href: string;
  icon: ReactNode;
};

function useCommandGroups(scope: CommandPaletteScope) {
  const {isLoggedIn, hasPermission} = useAuth();
  const settings = useSettings();
  const isAdmin = hasPermission('admin') || hasPermission('settings.update');

  return useMemo(() => {
    if (scope === 'admin' && isAdmin) {
      return buildAdminGroups();
    }

    const drive: PaletteItem[] = [
      item('drive', 'My files', '/drive', iconEl(driveSidebarIcons['/drive'])),
      item('recent', 'Recent', '/drive/recent', iconEl(driveSidebarIcons['/drive/recent'])),
      item('starred', 'Starred', '/drive/starred', iconEl(driveSidebarIcons['/drive/starred'])),
      item('shares', 'Shared with me', '/drive/shares', iconEl(driveSidebarIcons['/drive/shares'])),
      item('file-requests', 'File requests', '/drive/file-requests', iconEl(driveSidebarIcons['/drive/file-requests'])),
      item('delete-requests', 'Delete requests', '/drive/delete-requests', <TrashIcon className="size-4" />),
      item('trash', 'Trash', '/drive/trash', iconEl(driveSidebarIcons['/drive/trash'])),
      item('drive-search', 'Search Drive', '/drive/search', iconEl(driveSidebarIcons['/drive/search'])),
    ];

    const account: PaletteItem[] = [
      item('account', 'Account settings', '/account-settings', <UserIcon className="size-4" />),
      item('appearance', 'Appearance', '/account-settings/appearance', <PaletteIcon className="size-4" />),
      item('workspaces', 'Workspaces', '/account-settings/workspaces', <UsersIcon className="size-4" />),
      item('security', 'Password & login', '/account-settings/security', <LockIcon className="size-4" />),
    ];
    if (settings.notif?.subs?.integrated) {
      account.push(
        item(
          'notifications',
          'Notifications',
          '/account-settings/notifications',
          <BellIcon className="size-4" />,
        ),
      );
    }
    if (settings.billing?.enable) {
      account.push(
        item(
          'billing',
          'Plan & billing',
          '/account-settings/billing',
          <StoreIcon className="size-4" />,
        ),
      );
    }
    if (hasPermission('api.access')) {
      account.push(
        item(
          'api-keys',
          'API keys',
          '/account-settings/api-keys',
          <KeyIcon className="size-4" />,
        ),
      );
      account.push(
        item(
          'api-docs',
          'API reference',
          '/api-docs',
          <BookOpenIcon className="size-4" />,
        ),
      );
    }

    const groups = [];
    if (isLoggedIn) {
      if (scope === 'account') {
        groups.push({
          value: 'account',
          label: <Trans message="Account" />,
          items: account,
        });
        groups.push({
          value: 'drive',
          label: <Trans message="Drive" />,
          items: drive.slice(0, 4),
        });
      } else {
        groups.push({
          value: 'drive',
          label: <Trans message="Drive" />,
          items: drive,
        });
        groups.push({
          value: 'account',
          label: <Trans message="Account" />,
          items: account,
        });
      }
      if (isAdmin) {
        groups.push({
          value: 'admin-jump',
          label: <Trans message="Admin" />,
          items: [
            item('admin', 'Admin console', '/admin', <SettingsIcon className="size-4" />),
            item(
              'admin-settings',
              'Admin settings',
              '/admin/settings',
              <SettingsIcon className="size-4" />,
            ),
          ],
        });
      }
    } else {
      groups.push({
        value: 'site',
        label: <Trans message="Cloud Drive" />,
        items: [
          item('home', 'Home', '/', <HardDriveIcon className="size-4" />),
          item('pricing', 'Pricing', '/pricing', <StoreIcon className="size-4" />),
          item('login', 'Sign in', '/login', <UserIcon className="size-4" />),
        ],
      });
    }
    return groups;
  }, [
    hasPermission,
    isAdmin,
    isLoggedIn,
    scope,
    settings.billing?.enable,
    settings.notif?.subs?.integrated,
  ]);
}

function buildAdminGroups() {
  const overview: PaletteItem[] = [];
  const people: PaletteItem[] = [];
  const content: PaletteItem[] = [];
  const system: PaletteItem[] = [];

  for (const [href, icon] of Object.entries(AdminSidebarIcons)) {
    const label = adminLabel(href);
    const row = item(href, label, href, iconEl(icon as ReactElement));
    if (
      href.includes('users') ||
      href.includes('roles') ||
      href.includes('plans') ||
      href.includes('subscriptions')
    ) {
      people.push(row);
    } else if (
      href.includes('custom-pages') ||
      href.includes('/files') ||
      href.includes('/trash') ||
      href.includes('tags')
    ) {
      content.push(row);
    } else if (
      href.includes('localizations') ||
      href.includes('logs') ||
      href.includes('reports')
    ) {
      system.push(row);
    } else {
      overview.push(row);
    }
  }

  const settingsByGroup = SettingsNavGroups.map(group => {
    const pages = SettingsNavConfig.filter(
      (page): page is SettingsNavItem =>
        !!page && (page.group || 'site') === group.id,
    );
    return {
      value: `settings-${group.id}`,
      label: <Trans message={group.label.message} />,
      items: pages.map(page =>
        item(
          `settings-${page.to}`,
          typeof page.label === 'string' ? page.label : page.label.message,
          `/admin/settings/${page.to}`,
          page.icon ? (
            <page.icon className="size-4" />
          ) : (
            <SettingsIcon className="size-4" />
          ),
        ),
      ),
    };
  }).filter(group => group.items.length > 0);

  const groups = [];
  if (overview.length) {
    groups.push({
      value: 'overview',
      label: <Trans message="Overview" />,
      items: overview,
    });
  }
  if (people.length) {
    groups.push({
      value: 'people',
      label: <Trans message="People & plans" />,
      items: people,
    });
  }
  if (content.length) {
    groups.push({
      value: 'content',
      label: <Trans message="Content" />,
      items: content,
    });
  }
  groups.push(...settingsByGroup);
  if (system.length) {
    groups.push({
      value: 'system',
      label: <Trans message="System" />,
      items: system,
    });
  }
  return groups;
}

function item(
  id: string,
  label: string,
  href: string,
  icon: ReactNode,
  extra = '',
): PaletteItem {
  return {
    id,
    label,
    href,
    icon,
    search: `${label} ${href} ${id} ${extra}`.toLowerCase(),
  };
}

function iconEl(icon?: ReactElement) {
  if (!icon) return <HardDriveIcon className="size-4" />;
  return (
    <span className="inline-flex size-4 items-center justify-center [&>svg]:size-4">
      {icon}
    </span>
  );
}

function adminLabel(href: string): string {
  if (href === '/admin') return 'Admin console';
  if (href === '/admin/localizations') return 'Languages';
  if (href === '/admin/reports') return 'Analytics';
  if (href === '/admin/custom-pages') return 'Pages';
  return href
    .replace('/admin/', '')
    .replace(/-/g, ' ')
    .replace(/^\w/, c => c.toUpperCase());
}
